import React, {useState, useEffect, useCallback, useRef} from "react"
import {Box, Typography, Chip, Button, Skeleton, Collapse, Link, CircularProgress, Tooltip, Avatar} from "@mui/material"
import {alpha} from "@mui/material/styles"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import PersonIcon from "@mui/icons-material/Person"
import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import {format} from "date-fns"

import {DraftReplyPanel} from "./DraftReplyPanel"

import {api} from "@indebted/api"
import type {ConversationMessage, ConversationMessageAttachment, ConversationTimeline} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

const PAGE_SIZE = 5
const REPLY_SYNC_ATTEMPTS = 8
const REPLY_SYNC_INTERVAL_MS = 700

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

function formatOccurredAt(occurredAt: string): string {
	return format(new Date(occurredAt), "d MMM yyyy, HH:mm")
}

function formatAttachmentSize(sizeBytes: number): string {
	if (sizeBytes < 1024) return `${sizeBytes} B`
	if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`
	return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
}

// Each direction is a voice in the conversation: who speaks, the face beside
// the message, and whether the card carries the outbound indigo tint.
function senderProps(direction: string): {
	name: string
	avatar: React.ReactElement
	kindChip?: string
	outbound: boolean
} {
	const avatarSx = {width: 32, height: 32}
	switch (direction) {
		case "inbound":
			return {
				name: "Customer",
				avatar: (
					<Avatar sx={{...avatarSx, bgcolor: "secondary.main"}}>
						<PersonIcon fontSize="small" />
					</Avatar>
				),
				outbound: false,
			}
		case "agent-reply":
			return {
				name: "InDebted",
				avatar: (
					<Avatar sx={{...avatarSx, bgcolor: "primary.main"}}>
						<SupportAgentIcon fontSize="small" />
					</Avatar>
				),
				outbound: true,
			}
		case "information-request":
			return {
				name: "InDebted",
				avatar: (
					<Avatar sx={(theme) => ({...avatarSx, bgcolor: alpha(theme.palette.primary.main, 0.12)})}>
						<SmartToyIcon fontSize="small" color="primary" />
					</Avatar>
				),
				kindChip: "Information request",
				outbound: true,
			}
		default:
			return {
				name: "InDebted",
				avatar: (
					<Avatar sx={(theme) => ({...avatarSx, bgcolor: alpha(theme.palette.primary.main, 0.12)})}>
						<SmartToyIcon fontSize="small" color="primary" />
					</Avatar>
				),
				kindChip: "Auto-reply",
				outbound: true,
			}
	}
}

function deliveryChipProps(status: string): {label: string; color: "success" | "warning" | "error"} | null {
	switch (status) {
		case "delivered":
			return {label: "Delivered", color: "success"}
		case "temporarily-undelivered":
			return {label: "Delivery delayed", color: "warning"}
		case "permanently-undelivered":
			return {label: "Not delivered", color: "error"}
		default:
			return null
	}
}

// A reply row reuses the providerMessageId of the inbound message it answers, so
// the timeline can hold two rows sharing that id; identity has to include the
// direction (and occurredAt) to stay unique for React keys, refs, and highlight.
function rowKey(message: ConversationMessage): string {
	return `${message.direction}:${message.providerMessageId}:${message.occurredAt}`
}

type ReplyAction =
	| {kind: "draft"; onDraftReply: () => void}
	| {kind: "pick-up"; onPickUp: () => void; pickingUp: boolean}
	| {kind: "held-by-another-agent"}

function ReplySlot({action, isReplyTarget}: {action: ReplyAction; isReplyTarget: boolean}) {
	if (action.kind === "pick-up") {
		return (
			<Tooltip title="Pick up this case to reply">
				<span tabIndex={action.pickingUp ? 0 : undefined}>
					<Button
						variant="outlined"
						size="small"
						onClick={action.onPickUp}
						disabled={action.pickingUp}
						sx={{alignSelf: "flex-start"}}
					>
						PICK UP CASE
					</Button>
				</span>
			</Tooltip>
		)
	}

	if (action.kind === "held-by-another-agent") {
		return (
			<Tooltip title="This conversation's case is assigned to another agent">
				<span tabIndex={0}>
					<Button variant="outlined" size="small" disabled sx={{alignSelf: "flex-start"}}>
						Draft reply
					</Button>
				</span>
			</Tooltip>
		)
	}

	if (isReplyTarget) {
		return (
			<Typography role="status" variant="caption" color="primary" fontWeight={500}>
				Replying here
			</Typography>
		)
	}

	return (
		<Button variant="outlined" size="small" onClick={action.onDraftReply} sx={{alignSelf: "flex-start"}}>
			Draft reply
		</Button>
	)
}

function MessageRow({
	message,
	customerID,
	conversationID,
	threadSubject,
	isAnchor,
	isReplyTarget,
	targetIsEarlier,
	isHighlighted,
	replyToTarget,
	onJumpToTarget,
	replyAction,
	registerRef,
}: {
	message: ConversationMessage
	customerID: string
	conversationID: string
	threadSubject: string
	isAnchor: boolean
	isReplyTarget: boolean
	targetIsEarlier: boolean
	isHighlighted: boolean
	replyToTarget?: {rowKey: string; occurredAt: string}
	onJumpToTarget?: (rowKey: string) => void
	replyAction?: ReplyAction
	registerRef?: (element: HTMLElement | null) => void
}) {
	const [showQuoted, setShowQuoted] = useState(false)
	const {notification} = useNotification()
	const isInbound = message.direction === "inbound"
	const sender = senderProps(message.direction)
	const deliveryChip = message.deliveryStatus ? deliveryChipProps(message.deliveryStatus) : null
	const headerChip =
		isReplyTarget && targetIsEarlier ? (
			<Chip label="Replying to this" size="small" color="primary" />
		) : isAnchor ? (
			targetIsEarlier ? (
				<Chip label="Latest message" size="small" />
			) : (
				<Chip label="Reply anchor" size="small" color="primary" />
			)
		) : null

	const hasMetaRow =
		Boolean(headerChip) ||
		Boolean(message.direction === "agent-reply" && message.authoredBy) ||
		Boolean(isInbound && message.classificationCategory) ||
		Boolean(message.direction === "agent-reply" && message.edited) ||
		Boolean(deliveryChip)

	const downloadAttachment = async (attachment: ConversationMessageAttachment) => {
		try {
			// API Gateway only base64-decodes the response when the request asks for a
			// binary media type, so a plain navigation (text/html Accept) would save the
			// raw base64 text instead of the file.
			const response = await fetch(
				api.customer.getConversationMessageAttachmentURL(
					customerID,
					conversationID,
					message.providerMessageId,
					attachment.index,
				),
				{headers: {Accept: "application/octet-stream"}},
			)
			if (!response.ok) {
				notification.error("Failed to download the attachment.")
				return
			}
			const objectURL = URL.createObjectURL(await response.blob())
			const link = document.createElement("a")
			link.href = objectURL
			link.download = attachment.filename
			document.body.appendChild(link)
			link.click()
			link.remove()
			URL.revokeObjectURL(objectURL)
		} catch {
			notification.error("Failed to download the attachment.")
		}
	}

	return (
		<Box component="li" ref={registerRef} display="flex" gap={1.5} sx={{listStyle: "none"}}>
			{sender.avatar}
			<Box
				flex={1}
				minWidth={0}
				p={2.5}
				border="1px solid"
				borderColor={isReplyTarget ? "primary.main" : sender.outbound ? "transparent" : "grey.200"}
				borderRadius={2}
				sx={(theme) => ({
					transition: "background-color 300ms ease",
					bgcolor: isHighlighted
						? "action.selected"
						: sender.outbound
							? alpha(theme.palette.primary.main, 0.04)
							: "background.paper",
				})}
			>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.25}>
					<Box display="flex" alignItems="center" gap={1} minWidth={0} flexWrap="wrap">
						<Typography variant="subtitle2" color="textPrimary">
							{sender.name}
						</Typography>
						{sender.kindChip && <Chip label={sender.kindChip} size="small" variant="outlined" />}
					</Box>
					<Typography variant="caption" color="textSecondary" whiteSpace="nowrap" mt={0.25}>
						{formatOccurredAt(message.occurredAt)}
					</Typography>
				</Box>
				{message.subject && message.subject !== threadSubject && (
					<Typography variant="caption" color="textSecondary" display="block">
						Subject: <Box component="span">{message.subject}</Box>
					</Typography>
				)}
				{hasMetaRow && (
					<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
						{headerChip}
						{message.direction === "agent-reply" && message.authoredBy && (
							<Typography variant="caption" color="textSecondary">
								by {message.authoredBy}
							</Typography>
						)}
						{isInbound && message.classificationCategory && (
							<Chip label={message.classificationCategory} size="small" variant="outlined" />
						)}
						{message.direction === "agent-reply" && message.edited && (
							<Chip label="Edited" size="small" variant="outlined" />
						)}
						{deliveryChip && (
							<>
								<Chip
									label={deliveryChip.label}
									size="small"
									color={deliveryChip.color}
									variant="outlined"
								/>
								{message.deliveryStatus === "delivered" && message.deliveryOccurredAt && (
									<Typography variant="caption" color="textSecondary">
										at {formatOccurredAt(message.deliveryOccurredAt)}
									</Typography>
								)}
								{message.deliveryStatus !== "delivered" && message.deliveryReason && (
									<Typography variant="caption" color="textSecondary">
										· {message.deliveryReason}
									</Typography>
								)}
							</>
						)}
					</Box>
				)}
				{message.inReplyToProviderMessageId && (
					<Box mt={0.5}>
						{replyToTarget && onJumpToTarget ? (
							<Link
								component="button"
								type="button"
								variant="caption"
								underline="hover"
								onClick={() => onJumpToTarget(replyToTarget.rowKey)}
							>
								↳ In reply to the message from {formatOccurredAt(replyToTarget.occurredAt)}
							</Link>
						) : (
							<Typography variant="caption" color="textSecondary">
								↳ In reply to an earlier message
							</Typography>
						)}
					</Box>
				)}
				{message.body && (
					<Typography
						variant="body2"
						color="textPrimary"
						mt={1.5}
						sx={{whiteSpace: "pre-wrap", maxWidth: "80ch", lineHeight: 1.65}}
					>
						{message.body}
					</Typography>
				)}
				{message.attachments && message.attachments.length > 0 && (
					<Box mt={1} display="flex" flexWrap="wrap" gap={1}>
						{message.attachments.map((attachment) =>
							isInbound ? (
								<Chip
									key={attachment.index}
									label={attachment.filename}
									title={attachment.filename}
									size="small"
									icon={<AttachFileIcon />}
									onClick={() => downloadAttachment(attachment)}
								/>
							) : (
								<Box key={attachment.index} display="flex" alignItems="center" gap={0.5}>
									<Chip
										label={attachment.filename}
										title={attachment.filename}
										size="small"
										icon={<AttachFileIcon />}
									/>
									<Typography variant="caption" color="textSecondary">
										{formatAttachmentSize(attachment.sizeBytes)}
									</Typography>
								</Box>
							),
						)}
					</Box>
				)}
				{message.quoted && (
					<Box mt={1}>
						<Button
							variant="text"
							size="small"
							onClick={() => setShowQuoted((shown) => !shown)}
							sx={{alignSelf: "flex-start"}}
						>
							{showQuoted ? "Hide quoted text" : "Show quoted text"}
						</Button>
						<Collapse in={showQuoted} unmountOnExit>
							<Typography
								variant="body2"
								color="textSecondary"
								sx={{
									whiteSpace: "pre-wrap",
									borderLeft: "2px solid",
									borderColor: "grey.200",
									pl: 1.5,
									mt: 0.5,
								}}
							>
								{message.quoted}
							</Typography>
						</Collapse>
					</Box>
				)}
				{replyAction && (
					<Box mt={1}>
						<ReplySlot action={replyAction} isReplyTarget={isReplyTarget} />
					</Box>
				)}
			</Box>
		</Box>
	)
}

function ConversationDetail({
	customerID,
	conversationID,
	preferredCaseID,
	onPickedUp,
}: {
	customerID: string
	conversationID: string
	preferredCaseID?: string
	onPickedUp?: () => void
}) {
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()
	const [timeline, setTimeline] = useState<ConversationTimeline | null>(null)
	const [messages, setMessages] = useState<ConversationMessage[]>([])
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [selectedReplyMessageId, setSelectedReplyMessageId] = useState<string | null>(null)
	const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null)
	const [syncingReply, setSyncingReply] = useState(false)
	const [syncTimedOut, setSyncTimedOut] = useState(false)
	const [pickingUpCase, setPickingUpCase] = useState(false)

	const mounted = useRef(true)
	const messageRefs = useRef<Record<string, HTMLElement | null>>({})
	const highlightTimer = useRef<ReturnType<typeof setTimeout>>()
	const loadedCountRef = useRef(0)
	useEffect(() => {
		return () => {
			mounted.current = false
			clearTimeout(highlightTimer.current)
		}
	}, [])

	const jumpToMessage = useCallback((key: string) => {
		const element = messageRefs.current[key]
		if (!element) return
		element.scrollIntoView({behavior: "smooth", block: "center"})
		setHighlightedRowKey(key)
		clearTimeout(highlightTimer.current)
		highlightTimer.current = setTimeout(() => {
			if (mounted.current) setHighlightedRowKey(null)
		}, 1600)
	}, [])

	const fetchTimeline = useCallback(async () => {
		// Refetch covers every message already loaded, so it keeps the "Show earlier"
		// history instead of collapsing back to the first page.
		const conversation = await api.customer.getConversation(customerID, conversationID, {
			limit: Math.max(PAGE_SIZE, loadedCountRef.current),
			offset: 0,
			agentEmail,
		})
		if (!mounted.current) return null
		setTimeline(conversation)
		setMessages(conversation.messages)
		setHasMore(conversation.hasMore)
		loadedCountRef.current = conversation.messages.length
		return conversation
	}, [customerID, conversationID, agentEmail])

	const reloadConversation = useCallback(async () => {
		try {
			await fetchTimeline()
		} catch {
			if (mounted.current) notification.error("Failed to load the conversation")
		}
	}, [fetchTimeline, notification])

	const waitForSentReply = useCallback(
		async (repliedToProviderMessageId: string) => {
			// The reply is recorded synchronously but its timeline row is projected
			// asynchronously, so poll the refetch until the reply lands rather than
			// settling on a timeline that is briefly missing the reply just sent.
			setSyncingReply(true)
			setSyncTimedOut(false)
			try {
				for (let attempt = 0; attempt < REPLY_SYNC_ATTEMPTS; attempt++) {
					try {
						const conversation = await fetchTimeline()
						if (!conversation) return
						const landed = conversation.messages.some(
							(m) =>
								m.direction === "agent-reply" &&
								m.inReplyToProviderMessageId === repliedToProviderMessageId,
						)
						if (landed) return
					} catch {
						// A transient read failure should not abort the sync; keep polling.
					}
					if (attempt < REPLY_SYNC_ATTEMPTS - 1) await delay(REPLY_SYNC_INTERVAL_MS)
				}
				// Polling settled without the reply: the send succeeded but the projection
				// is still catching up, so flag that the timeline may not show it yet.
				if (mounted.current) setSyncTimedOut(true)
			} finally {
				if (mounted.current) setSyncingReply(false)
			}
		},
		[fetchTimeline],
	)

	useEffect(() => {
		if (!agentEmail) return
		setLoading(true)
		reloadConversation().finally(() => {
			if (mounted.current) setLoading(false)
		})
	}, [reloadConversation, agentEmail])

	async function pickUpLinkedCase(caseID: string, email: string, name: string) {
		setPickingUpCase(true)
		try {
			await api.customer.pickUpCase(customerID, caseID, {AgentEmail: email, AgentName: name})
			notification.success("Case picked up")
		} catch (error: any) {
			notification.error(error?.Message ?? "Failed to pick up case")
		}
		// Two agents can race for the same case, so both outcomes settle against the server.
		await reloadConversation()
		onPickedUp?.()
		if (mounted.current) setPickingUpCase(false)
	}

	function showEarlier() {
		setLoadingMore(true)
		api.customer
			.getConversation(customerID, conversationID, {limit: PAGE_SIZE, offset: messages.length})
			.then((conversation) => {
				setMessages((current) => [...current, ...conversation.messages])
				setHasMore(conversation.hasMore)
				loadedCountRef.current += conversation.messages.length
			})
			.catch(() => {
				notification.error("Failed to load earlier messages")
			})
			.finally(() => {
				setLoadingMore(false)
			})
	}

	if (loading) {
		return (
			<Box role="status" aria-label="Loading conversation" display="flex" flexDirection="column" gap={2}>
				{[0, 1].map((i) => (
					<Box key={i} display="flex" gap={1.5}>
						<Skeleton variant="circular" width={32} height={32} />
						<Box flex={1} p={2.5} border="1px solid" borderColor="grey.200" borderRadius={2}>
							<Box display="flex" justifyContent="space-between" mb={1.5}>
								<Skeleton variant="text" width={160} />
								<Skeleton variant="text" width={90} />
							</Box>
							<Skeleton variant="text" width="90%" />
							<Skeleton variant="text" width="75%" />
						</Box>
					</Box>
				))}
			</Box>
		)
	}

	if (!timeline) {
		return null
	}

	const replyTarget = selectedReplyMessageId ?? timeline.replyAnchorProviderMessageId
	const targetIsEarlier = Boolean(replyTarget) && replyTarget !== timeline.replyAnchorProviderMessageId
	const inboundById = new Map(messages.filter((m) => m.direction === "inbound").map((m) => [m.providerMessageId, m]))
	// Messages render newest first, so the newest subject titles the thread.
	const threadSubject = messages.length > 0 ? messages[0].subject : ""

	// A backend that predates case ownership answers without these fields, and a
	// conversation that cannot be read is worse than one that cannot be gated.
	const linkedCases = timeline.linkedCases ?? []
	const ownershipGated = timeline.caseOwnershipEnabled && linkedCases.length > 0
	const ownsLinkedCase = linkedCases.some((linked) => !!linked.assigneeEmail && linked.assigneeEmail === agentEmail)
	const unassignedLinkedCase =
		linkedCases.find((linked) => linked.caseId === preferredCaseID && !linked.assigneeEmail) ??
		linkedCases.find((linked) => !linked.assigneeEmail)
	const canDraftReply = !ownershipGated || ownsLinkedCase

	const replyActionFor = (message: ConversationMessage): ReplyAction | undefined => {
		if (message.direction !== "inbound") return undefined
		if (canDraftReply) {
			return {kind: "draft", onDraftReply: () => setSelectedReplyMessageId(message.providerMessageId)}
		}
		if (!unassignedLinkedCase) return {kind: "held-by-another-agent"}
		if (!agentEmail || !agentName) return undefined
		return {
			kind: "pick-up",
			pickingUp: pickingUpCase,
			onPickUp: () => void pickUpLinkedCase(unassignedLinkedCase.caseId, agentEmail, agentName),
		}
	}

	return (
		<Box
			sx={{borderTop: "1px solid", borderColor: "grey.200", pt: 2.5}}
			display="flex"
			flexDirection="column"
			gap={2}
		>
			{threadSubject && (
				<Typography variant="subtitle1" component="h3" fontWeight={600} color="textPrimary">
					{threadSubject}
				</Typography>
			)}
			<Box display="flex" gap={3} alignItems="flex-start">
				<Box flex={1} display="flex" flexDirection="column" gap={1.5} minWidth={0}>
					{syncingReply && (
						<Box role="status" display="flex" alignItems="center" gap={1}>
							<CircularProgress size={14} />
							<Typography variant="caption" color="textSecondary">
								Updating timeline…
							</Typography>
						</Box>
					)}
					{syncTimedOut && !syncingReply && (
						<Typography role="status" variant="caption" color="textSecondary">
							Reply sent. It is taking longer than usual to appear, refresh to confirm.
						</Typography>
					)}
					<Box component="ul" display="flex" flexDirection="column" gap={2} m={0} p={0}>
						{messages.map((m) => {
							const answeredMessage = m.inReplyToProviderMessageId
								? inboundById.get(m.inReplyToProviderMessageId)
								: undefined
							const key = rowKey(m)
							const isInbound = m.direction === "inbound"
							return (
								<MessageRow
									key={key}
									message={m}
									customerID={customerID}
									conversationID={conversationID}
									threadSubject={threadSubject}
									isAnchor={
										isInbound && m.providerMessageId === timeline.replyAnchorProviderMessageId
									}
									isReplyTarget={isInbound && m.providerMessageId === replyTarget}
									targetIsEarlier={targetIsEarlier}
									isHighlighted={highlightedRowKey === key}
									replyToTarget={
										answeredMessage
											? {rowKey: rowKey(answeredMessage), occurredAt: answeredMessage.occurredAt}
											: undefined
									}
									onJumpToTarget={jumpToMessage}
									replyAction={replyActionFor(m)}
									registerRef={(element) => {
										messageRefs.current[key] = element
									}}
								/>
							)
						})}
						{hasMore && (
							<Button
								variant="text"
								size="small"
								onClick={showEarlier}
								disabled={loadingMore}
								sx={{alignSelf: "flex-start"}}
							>
								{loadingMore ? "Loading…" : "Show earlier"}
							</Button>
						)}
					</Box>
				</Box>
				{replyTarget && canDraftReply && (
					<Box flex={1} minWidth={0} maxWidth={960}>
						<DraftReplyPanel
							key={replyTarget}
							customerID={customerID}
							conversationID={conversationID}
							providerMessageId={replyTarget}
							repliesToEarlierMessage={targetIsEarlier}
							autoGenerate={ownershipGated && ownsLinkedCase}
							onSent={() => {
								// Pin the panel to the message just answered, or an inbound
								// arriving during the sync would move the anchor, remount the
								// panel, and replace the confirmation with an unasked draft.
								setSelectedReplyMessageId(replyTarget)
								void waitForSentReply(replyTarget)
							}}
						/>
					</Box>
				)}
			</Box>
		</Box>
	)
}

export {ConversationDetail}
